import React from 'react'
import { Document, Page, Text, View, Image, Font } from '@react-pdf/renderer'
import {
    ClientFormFieldsFragment,
    ClinicFieldsFragment,
    FormElementInput,
    FormElementType,
    PatientDisplayFieldsFragment,
} from '~graphql/generated/graphql'
import { formatClinicName, getClientFullName } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import { usePDFStyleSheet } from '~utils/hooks/use-pdf-stylesheet'
import { useFormatDate } from '~utils/hooks/use-format-date'
import { DateFormat } from '~utils/shared-types'
import { getFormFileAttachments } from '~components/engagement/forms/edit-form/editor/form-attachment-helpers'
import {
    FORM_RICH_TEXT_HIGHLIGHT_COLOR,
    hasRichTextFormatting,
    richTextToPlainText,
    richTextToSegments,
} from '~utils/form-rich-text'

type PdfTextStyle = React.ComponentProps<typeof Text>['style']

let hasRegisteredFormPdfFonts = false

export function registerFormPdfFonts() {
    if (hasRegisteredFormPdfFonts || !Font?.register) return

    Font.register({
        family: 'Roboto',
        fonts: [
            {
                src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
                fontWeight: 300,
            },
            {
                src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
                fontWeight: 400,
            },
            {
                src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf',
                fontWeight: 400,
                fontStyle: 'italic',
            },
            {
                src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
                fontWeight: 500,
            },
            {
                src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
                fontWeight: 700,
            },
            {
                src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bolditalic-webfont.ttf',
                fontWeight: 700,
                fontStyle: 'italic',
            },
        ],
    })

    hasRegisteredFormPdfFonts = true
}

function getRichTextSegmentStyle(segment: {
    isBold?: boolean
    isItalic?: boolean
    isHighlighted?: boolean
}): PdfTextStyle {
    return {
        fontWeight: segment.isBold ? 700 : 400,
        ...(segment.isItalic ? { fontStyle: 'italic' } : {}),
        ...(segment.isHighlighted
            ? { backgroundColor: FORM_RICH_TEXT_HIGHLIGHT_COLOR }
            : {}),
    }
}

const renderRichTextPdfText = (
    content: string | null | undefined,
    style: PdfTextStyle,
) => (
    <Text style={style}>
        {richTextToSegments(content).map((segment, index) => (
            <Text
                key={`${segment.text}-${index}`}
                style={getRichTextSegmentStyle(segment)}
            >
                {segment.text}
            </Text>
        ))}
    </Text>
)

const renderHeaderPdfText = (
    content: string | null | undefined,
    style: PdfTextStyle,
) => {
    if (!hasRichTextFormatting(content)) {
        return (
            <Text style={[style, { fontWeight: 700 }]}>
                {richTextToPlainText(content)}
            </Text>
        )
    }

    return renderRichTextPdfText(content, [style, { fontWeight: 400 }])
}

// Helper function to render form elements
const renderFormElement = (
    element: FormElementInput,
    styles: Record<string, any>,
) => {
    const content = richTextToPlainText(element.content)

    switch (element.type) {
        case FormElementType.Header:
            return (
                <View wrap={false}>
                    {renderHeaderPdfText(element.content, {
                        fontFamily: 'Roboto',
                        fontSize: 14,
                        color: '#2C3E50',
                        borderBottomWidth: 1,
                        borderBottomColor: '#3498DB',
                        paddingBottom: 4,
                        marginTop: 12,
                        marginBottom: 8,
                    })}
                </View>
            )
        case FormElementType.MediumHeader:
            return (
                <View wrap={false}>
                    {renderHeaderPdfText(element.content, {
                        fontFamily: 'Roboto',
                        fontSize: 13,
                        color: '#34495E',
                        marginTop: 10,
                        marginBottom: 6,
                    })}
                </View>
            )
        case FormElementType.Subtitle:
            return (
                <View wrap={false}>
                    {renderHeaderPdfText(element.content, {
                        fontFamily: 'Roboto',
                        fontSize: 12,
                        color: '#34495E',
                        marginTop: 8,
                        marginBottom: 4,
                    })}
                </View>
            )
        case FormElementType.ShortAnswer:
            return (
                <View style={styles.formElement} wrap={false}>
                    <Text
                        style={[
                            styles.label,
                            {
                                fontSize: 10,
                                color: '#7F8C8D',
                                marginBottom: 3,
                            },
                        ]}
                    >
                        {content}
                    </Text>
                    <View
                        style={[
                            styles.input,
                            {
                                borderWidth: 0.5,
                                borderColor: '#BDC3C7',
                                backgroundColor: '#FFFFFF',
                                padding: 6,
                                minHeight: 24,
                            },
                        ]}
                    >
                        <Text style={{ fontSize: 10, color: '#2C3E50' }}>
                            {element.value || ''}
                        </Text>
                    </View>
                </View>
            )
        case FormElementType.ParagraphAnswer:
            return (
                <View style={styles.formElement} wrap={false}>
                    <Text
                        style={[
                            styles.label,
                            {
                                fontSize: 10,
                                color: '#7F8C8D',
                                marginBottom: 3,
                            },
                        ]}
                    >
                        {content}
                    </Text>
                    <View
                        style={[
                            styles.textarea,
                            {
                                borderWidth: 0.5,
                                borderColor: '#BDC3C7',
                                backgroundColor: '#FFFFFF',
                                padding: 6,
                                minHeight: 60,
                            },
                        ]}
                    >
                        <Text style={{ fontSize: 10, color: '#2C3E50' }}>
                            {element.value || ''}
                        </Text>
                    </View>
                </View>
            )
        case FormElementType.Checkbox:
            const isChecked = element.value === 'true'
            return (
                <View
                    style={[
                        styles.checkbox,
                        {
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginVertical: 2,
                        },
                    ]}
                    wrap={false}
                >
                    <View
                        style={[
                            styles.checkboxBox,
                            {
                                width: 10,
                                height: 10,
                                borderWidth: 0.5,
                                borderColor: '#95A5A6',
                                marginRight: 6,
                                backgroundColor: '#FFFFFF',
                            },
                        ]}
                    >
                        {isChecked && (
                            <View
                                style={[
                                    styles.checkboxChecked,
                                    {
                                        width: 6,
                                        height: 6,
                                        margin: 1,
                                        backgroundColor: '#3498DB',
                                    },
                                ]}
                            />
                        )}
                    </View>
                    <Text style={{ fontSize: 10, color: '#2C3E50' }}>
                        {content}
                    </Text>
                </View>
            )
        case FormElementType.SelectOne:
            return (
                <View style={styles.formElement} wrap={false}>
                    <Text
                        style={[
                            styles.label,
                            {
                                fontSize: 10,
                                color: '#7F8C8D',
                                marginBottom: 3,
                            },
                        ]}
                    >
                        {content}
                    </Text>
                    <View style={[styles.radioGroup, { marginLeft: 4 }]}>
                        {element.options?.map((option, index) => {
                            const isSelected = element.value === option
                            return (
                                <View
                                    key={index}
                                    style={[
                                        styles.radio,
                                        {
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            marginVertical: 2,
                                        },
                                    ]}
                                >
                                    <View
                                        style={[
                                            styles.radioCircle,
                                            {
                                                width: 10,
                                                height: 10,
                                                borderRadius: 5,
                                                borderWidth: 0.5,
                                                borderColor: '#95A5A6',
                                                marginRight: 6,
                                                backgroundColor: '#FFFFFF',
                                            },
                                        ]}
                                    >
                                        {isSelected && (
                                            <View
                                                style={[
                                                    styles.radioChecked,
                                                    {
                                                        width: 6,
                                                        height: 6,
                                                        margin: 1,
                                                        borderRadius: 3,
                                                        backgroundColor:
                                                            '#3498DB',
                                                    },
                                                ]}
                                            />
                                        )}
                                    </View>
                                    <Text
                                        style={{
                                            fontSize: 10,
                                            color: '#2C3E50',
                                        }}
                                    >
                                        {option}
                                    </Text>
                                </View>
                            )
                        })}
                    </View>
                </View>
            )
        case FormElementType.SelectMultiple:
            const selectedOptions = element.value
                ? JSON.parse(element.value)
                : []
            return (
                <View style={styles.formElement} wrap={false}>
                    <Text
                        style={[
                            styles.label,
                            {
                                fontSize: 10,
                                color: '#7F8C8D',
                                marginBottom: 3,
                            },
                        ]}
                    >
                        {content}
                    </Text>
                    {element.options?.map((option, index) => {
                        const isChecked = selectedOptions.includes(option)
                        return (
                            <View
                                key={index}
                                style={[
                                    styles.checkbox,
                                    {
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        marginVertical: 2,
                                    },
                                ]}
                            >
                                <View
                                    style={[
                                        styles.checkboxBox,
                                        {
                                            width: 10,
                                            height: 10,
                                            borderWidth: 0.5,
                                            borderColor: '#95A5A6',
                                            marginRight: 6,
                                            backgroundColor: '#FFFFFF',
                                        },
                                    ]}
                                >
                                    {isChecked && (
                                        <View
                                            style={[
                                                styles.checkboxChecked,
                                                {
                                                    width: 6,
                                                    height: 6,
                                                    margin: 1,
                                                    backgroundColor: '#3498DB',
                                                },
                                            ]}
                                        />
                                    )}
                                </View>
                                <Text
                                    style={{ fontSize: 10, color: '#2C3E50' }}
                                >
                                    {option}
                                </Text>
                            </View>
                        )
                    })}
                </View>
            )
        case FormElementType.Signature:
            return (
                <View style={styles.formElement} wrap={false}>
                    <Text
                        style={[
                            styles.label,
                            {
                                fontSize: 10,
                                color: '#7F8C8D',
                                marginBottom: 3,
                            },
                        ]}
                    >
                        {content}
                    </Text>
                    {element.value ? (
                        <Image
                            style={[
                                styles.signature,
                                {
                                    height: 100,
                                    width: 250,
                                    marginTop: 2,
                                    borderWidth: 0.5,
                                    borderColor: '#BDC3C7',
                                    backgroundColor: '#FFFFFF',
                                },
                            ]}
                            src={element.value}
                        />
                    ) : (
                        <View
                            style={[
                                styles.signature,
                                {
                                    height: 100,
                                    width: 250,
                                    marginTop: 2,
                                    borderWidth: 0.5,
                                    borderColor: '#BDC3C7',
                                    backgroundColor: '#FFFFFF',
                                },
                            ]}
                        />
                    )}
                </View>
            )
        case FormElementType.FileUpload: {
            const attachments = getFormFileAttachments(element)

            return (
                <View style={styles.formElement} wrap={false}>
                    <Text
                        style={[
                            styles.label,
                            {
                                fontSize: 10,
                                color: '#7F8C8D',
                                marginBottom: 3,
                            },
                        ]}
                    >
                        {content}
                    </Text>
                    {attachments.length ? (
                        attachments.map(attachment => (
                            <Text
                                key={attachment.url}
                                style={{ fontSize: 10, color: '#2C3E50' }}
                            >
                                {attachment.filename || attachment.url}
                            </Text>
                        ))
                    ) : (
                        <Text style={{ fontSize: 10, color: '#2C3E50' }}>
                            No files attached
                        </Text>
                    )}
                </View>
            )
        }
        default:
            return null
    }
}

// Function to organize elements
const organizeElements = (elements: FormElementInput[]) => {
    const rows: FormElementInput[][] = []

    elements.forEach(element => {
        const columnOrder = element.column_order || 0
        if (!rows[columnOrder]) {
            rows[columnOrder] = []
        }
        rows[columnOrder].push(element)
    })

    // Sort elements within each row by row_order
    rows.forEach(row => {
        row.sort((a, b) => (a.row_order || 0) - (b.row_order || 0))
    })

    return rows
}

// Client/Patient Info Component
const ClientPatientInfo = ({
    client,
    patient,
    clinic,
    styles,
}: {
    client?: ClientFormFieldsFragment['client']
    patient?: PatientDisplayFieldsFragment | null
    clinic?: ClinicFieldsFragment
    styles: Record<string, any>
}) => {
    const { formatDate } = useFormatDate({ clinicOverride: clinic })

    const formatBirthday = (timestamp: number | null | undefined): string => {
        if (!timestamp) return 'N/A'
        const date = new Date(timestamp)
        return formatDate(date, DateFormat.Date)
    }

    const blankValueStyle = {
        ...styles.value,
        marginTop: 18,
        marginBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
        marginRight: 30,
    }

    return (
        <View style={[styles.clientPatientSection, { marginVertical: 5 }]}>
            <View style={styles.infoGrid}>
                {client && (
                    <>
                        <View style={[styles.infoItem, { width: '100%' }]}>
                            <Text style={styles.sectionTitle}>
                                Client Information
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Name</Text>
                            <Text style={styles.value}>
                                {getClientFullName(client, true) || ''}
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Phone</Text>
                            <Text style={styles.value}>
                                {formatPhoneReadable(
                                    client.phone,
                                    client?.address?.country ||
                                        clinic?.address?.country,
                                )}
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Email</Text>
                            <Text style={styles.value}>{client.email}</Text>
                        </View>
                        {client.address &&
                            client.address.street_a &&
                            client.address.city &&
                            client.address.state &&
                            client.address.zip && (
                                <View
                                    style={[styles.infoItem, { width: '100%' }]}
                                >
                                    <Text style={styles.label}>Address</Text>
                                    <Text style={styles.value}>
                                        {`${client.address.street_a}, ${client.address.city}, ${client.address.state} ${client.address.zip}`}
                                    </Text>
                                </View>
                            )}
                    </>
                )}
                {!client && (
                    <>
                        <View style={[styles.infoItem, { width: '100%' }]}>
                            <Text style={styles.sectionTitle}>
                                Client Information
                            </Text>
                        </View>
                        <View style={[styles.infoItem, { width: '66%' }]}>
                            <Text style={styles.label}>Name</Text>
                            <Text style={blankValueStyle}></Text>
                        </View>
                        <View style={[styles.infoItem, { width: '33%' }]}>
                            <Text style={styles.label}>Phone</Text>
                            <Text style={blankValueStyle}></Text>
                        </View>
                        <View style={[styles.infoItem, { width: '100%' }]}>
                            <Text style={styles.label}>Email</Text>
                            <Text style={blankValueStyle}></Text>
                        </View>

                        <View style={[styles.infoItem, { width: '100%' }]}>
                            <Text style={styles.label}>Address</Text>
                            <Text style={blankValueStyle}></Text>
                        </View>
                    </>
                )}
            </View>

            {patient && (
                <>
                    <View style={[styles.divider, { marginVertical: 3 }]} />
                    <View style={styles.infoGrid}>
                        <View style={[styles.infoItem, { width: '100%' }]}>
                            <Text style={styles.sectionTitle}>
                                Patient Information
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Name</Text>
                            <Text style={styles.value}>
                                {patient.name || 'N/A'}
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Birthday</Text>
                            <Text style={styles.value}>
                                {formatBirthday(patient.birthday)}
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Sex</Text>
                            <Text style={styles.value}>
                                {patient.gender || 'N/A'}
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Species</Text>
                            <Text style={styles.value}>
                                {patient.clinic_species?.description || 'N/A'}
                            </Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Breed</Text>
                            <Text style={styles.value}>
                                {patient.clinic_breed?.description || 'N/A'}
                            </Text>
                        </View>
                    </View>
                </>
            )}
        </View>
    )
}

// Main PDF component
const FormPDF = ({
    refreshToken,
    elements,
    formName,
    logoSrc,
    clinic,
    client,
    patient,
    showClientInfo,
}: {
    refreshToken?: string
    elements: FormElementInput[]
    formName: string
    logoSrc?: string
    clinic?: ClinicFieldsFragment
    client?: ClientFormFieldsFragment['client']
    patient?: PatientDisplayFieldsFragment | null
    showClientInfo?: boolean
}) => {
    const organizedRows = organizeElements(elements)
    const styles = usePDFStyleSheet()

    return (
        <Document key={refreshToken}>
            <Page size="A4" style={styles.page}>
                <View
                    style={[
                        styles.header,
                        {
                            borderBottomWidth: 1,
                            borderBottomColor: '#e0e0e0',
                            marginBottom: 3,
                            paddingBottom: 3,
                        },
                    ]}
                >
                    <View style={styles.headerContent}>
                        {logoSrc && <Image style={styles.logo} src={logoSrc} />}
                        {clinic && (
                            <View style={styles.clinicInfo}>
                                <Text style={styles.clinicName}>
                                    {formatClinicName(clinic.name)}
                                </Text>
                                <Text style={styles.clinicContact}>
                                    {formatPhoneReadable(
                                        clinic.phone,
                                        clinic?.address?.country,
                                    )}
                                </Text>
                                <Text style={styles.clinicContact}>
                                    {clinic.email}
                                </Text>
                                <Text style={styles.clinicContact}>
                                    {clinic.website}
                                </Text>
                                {clinic.address && (
                                    <View style={styles.clinicInfo}>
                                        <Text style={styles.clinicContact}>
                                            {clinic.address?.street_a}
                                            {clinic.address?.street_b &&
                                                `, ${clinic.address?.street_b}`}
                                        </Text>
                                        <Text style={styles.clinicContact}>
                                            {clinic.address?.city},{' '}
                                            {clinic.address?.state}{' '}
                                            {clinic.address?.zip}
                                        </Text>
                                    </View>
                                )}
                            </View>
                        )}
                    </View>
                </View>

                <Text
                    style={[
                        styles.title,
                        {
                            textAlign: 'center',
                            marginVertical: 2,
                            fontSize: 16,
                        },
                    ]}
                >
                    {formName}
                </Text>

                {showClientInfo && (
                    <ClientPatientInfo
                        client={client}
                        patient={patient}
                        clinic={clinic}
                        styles={styles}
                    />
                )}

                <View style={styles.section}>
                    {organizedRows.map((row, rowIndex) =>
                        row && row.length > 0 ? (
                            <View
                                key={rowIndex}
                                wrap={false} // prevent awkward row splits across pages
                                style={[styles.row, { marginBottom: 3 }]}
                            >
                                {row.map((element, elementIndex) => (
                                    <View
                                        key={elementIndex}
                                        style={[
                                            styles.column,
                                            {
                                                marginHorizontal: 1,
                                                marginBottom: 1,
                                                padding: 1,
                                            },
                                        ]}
                                    >
                                        {renderFormElement(element, styles)}
                                    </View>
                                ))}
                            </View>
                        ) : null,
                    )}
                </View>

                {/* Footer with page numbers */}
                <Text
                    style={{
                        position: 'absolute',
                        bottom: 30,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        fontSize: 8,
                        color: '#95A5A6',
                        fontWeight: 300,
                    }}
                    render={({ pageNumber, totalPages }) =>
                        `Page ${pageNumber} of ${totalPages}`
                    }
                    fixed
                />
            </Page>
        </Document>
    )
}

export default FormPDF
